import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Users } from "lucide-react";
import { Dropdown } from "../../../../shared/ui/dropdown/Dropdown";
import { usePortalRecords } from "../../application/usePortalAuth";
import {
  panelChoice,
  recordKind,
  recordLabel,
  recordSubtitle,
  recordSubtitleKind,
} from "../../domain/recordLabel";

/**
 * Choosing which panel to look at.
 *
 * Rendered only when the account holds more than one record. A patient with a
 * single therapist sees no switcher at all — there is nothing to switch to,
 * and an empty menu is worse than no menu.
 */
export default function PortalPanelSwitcher() {
  const { t } = useTranslation();
  const { records, activePatientId, switchTo } = usePortalRecords();
  const [isOpen, setIsOpen] = useState(false);

  if (records.length <= 1) return null;

  const active = records.find((r) => r.patient_id === activePatientId) ?? records[0];
  const heading = {
    course: t("portal.switcher.chooseCourse", "Choose your course"),
    professional: t("portal.switcher.chooseProfessional", "Choose your therapist"),
    mentor: t("portal.switcher.chooseMentor", "Choose your mentor"),
    panel: t("portal.switcher.chooseRecord", "Your panels"),
  }[panelChoice(records)];

  const choose = (patientId: string) => {
    switchTo(patientId);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t("portal.switcher.label", "Change panel")}
        onClick={() => setIsOpen((open) => !open)}
        className="dropdown-toggle flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-content-primary hover:bg-surface-secondary focus-visible:outline-2 focus-visible:outline-brand-500"
      >
        <Users className="w-4 h-4 shrink-0" aria-hidden="true" />
        <span className="max-w-[12rem] truncate">{recordLabel(active)}</span>
        <ChevronDown className="w-4 h-4 shrink-0" aria-hidden="true" />
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        className="right-0 mt-1 min-w-[16rem] p-1"
      >
        <p className="px-3 py-2 text-xs uppercase tracking-wide text-content-tertiary">
          {heading}
        </p>
        <ul role="menu" className="flex flex-col">
          {records.map((record) => (
            <li key={record.patient_id} role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => choose(record.patient_id)}
                aria-current={record.patient_id === activePatientId ? "true" : undefined}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-surface-secondary focus-visible:outline-2 focus-visible:outline-brand-500"
              >
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate font-medium text-content-primary">
                      {recordLabel(record)}
                    </span>
                    <span className="shrink-0 rounded bg-gray-100 dark:bg-white/5 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-content-tertiary">
                      {t(`portal.recordKind.${recordKind(record)}`)}
                    </span>
                  </span>
                  {recordSubtitle(record) && (
                    <span className="flex items-center gap-2 text-xs text-content-tertiary">
                      <span className="truncate">{recordSubtitle(record)}</span>
                      {recordSubtitleKind(record) && (
                        <span className="shrink-0 uppercase tracking-wide text-xs">
                          {t(`portal.recordKind.${recordSubtitleKind(record)}`)}
                        </span>
                      )}
                    </span>
                  )}
                </span>
                {record.inactive && (
                  <span className="shrink-0 rounded-full bg-surface-secondary px-2 py-0.5 text-xs text-content-tertiary">
                    {t("portal.switcher.inactiveBadge", "Inactive")}
                  </span>
                )}
                {record.patient_id === activePatientId && (
                  <Check className="w-4 h-4 shrink-0 text-brand-500" aria-hidden="true" />
                )}
              </button>
            </li>
          ))}
        </ul>
      </Dropdown>
    </div>
  );
}
